import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, model, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, switchMap } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { DividerModule } from 'primeng/divider';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { FuncionarioService } from '@/core/api/sicom-api/sistema/funcionario/funcionario.service';
import { FuncionarioRequest } from '@/core/api/sicom-api/sistema/funcionario/funcionario.model';
import { UsuarioService } from '@/core/api/sicom-api/sistema/usuario/usuario.service';
import { SolicitanteSearchResponse } from '@/core/api/sicom-api/sistema/usuario/usuario.model';
import { EstruturaService } from '@/core/api/sicom-api/sistema/estrutura/estrutura.service';
import { CargoService } from '@/core/api/sicom-api/sistema/cargo/cargo.service';
import { NotificacoesService } from '@/core/services/notificacoes.service';
import { PermissionService } from '@/core/permissions/permission.service';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { MascaraUtil } from '@/shared/utils/mascara-util';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
/** Chave composta do funcionário. `null` no input significa criação. */
export interface FuncionarioChave {
    uf: string;
    idSolicitante: number;
}

/** A pessoa escolhida para virar funcionária, ou a já cadastrada em modo de edição. */
export interface SolicitanteEscolhido {
    idSolicitante: number;
    uf: string;
    nmSolicitante: string;
    cpf: string | null;
}

type Passo = 'buscar-solicitante' | 'formulario';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-funcionario-detalhe',
    standalone: true,
    imports: [
        DialogoPadraoDirective,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        ButtonModule,
        Dialog,
        InputTextModule,
        IconFieldModule,
        InputIconModule,
        SelectModule,
        DatePickerModule,
        ToggleSwitchModule,
        DividerModule,
        TagModule,
        TableModule,
        SelectEstado
    ],
    templateUrl: './funcionario-detalhe.html',
    styleUrl: './funcionario-detalhe.scss'
})
/**
 * Criação e edição do cadastro de funcionário.
 *
 * <p>O <b>Agente de Registro é caso à parte</b> e segue o mesmo padrão do diálogo de usuário: não
 * aparece na criação (sem linha em `FUNCIONARIO` o endpoint recusaria com 422), e na edição grava na
 * hora, pelo endpoint dedicado, <b>fora</b> do salvamento do formulário — a role é outra. Sem a
 * role, o controle fica desabilitado com o motivo ao lado; o gate é o `[disabled]`, nunca esconder.
 *
 * <p><b>Não existe excluir.</b> A saída da instituição é a data de afastamento, campo do formulário.
 */
export class FuncionarioDetalhe {
    protected readonly roleUsuariosEditarAgr = PERMISSOES.configuracoes.usuarios.editarAgr;

    private readonly fb = inject(FormBuilder);
    private readonly service = inject(FuncionarioService);
    private readonly usuarioService = inject(UsuarioService);
    private readonly estruturaService = inject(EstruturaService);
    private readonly cargoService = inject(CargoService);
    private readonly notificacoes = inject(NotificacoesService);
    private readonly permissionService = inject(PermissionService);
    private readonly destroyRef = inject(DestroyRef);

    chave = input<FuncionarioChave | null>(null);
    showDialog = model.required<boolean>();
    salvo = output<void>();
    /**
     * O AGR grava fora do salvamento, então sem este aviso a lista continuaria exibindo a marca
     * antiga se o usuário fechasse o diálogo por Cancelar.
     */
    agrAlterado = output<void>();

    protected readonly passo = signal<Passo>('buscar-solicitante');
    protected readonly salvando = signal(false);
    protected readonly carregando = signal(false);
    protected readonly solicitante = signal<SolicitanteEscolhido | null>(null);
    protected formSubmitted = false;

    protected form: FormGroup = this.novoForm();

    // ── Busca de solicitante (só na criação) ───────────────────────────────
    protected readonly termoBusca = signal('');
    protected readonly ufBusca = signal('');
    protected readonly buscando = signal(false);
    protected readonly resultados = signal<SolicitanteSearchResponse[]>([]);
    protected readonly totalResultados = signal(0);
    protected readonly buscou = signal(false);
    /** O default do serviço são 15, poucos demais para nomes comuns numa UF grande. */
    private readonly TAMANHO_BUSCA = 50;

    // ── Listas de apoio ────────────────────────────────────────────────────
    protected readonly opcoesCargos = signal<{ label: string; value: number }[]>([]);
    protected readonly opcoesEstruturas = signal<{ label: string; value: number }[]>([]);

    // ── Agente de Registro ─────────────────────────────────────────────────
    protected readonly agenteAgr = signal(false);
    protected readonly salvandoAgr = signal(false);

    /**
     * O toggle só existe em edição: na criação não há linha em `FUNCIONARIO` para marcar, e o
     * endpoint recusaria com 422. Depois de criar, o funcionário é reaberto para marcar.
     */
    protected readonly agrVisivel = computed(() => this.chave() !== null);

    /**
     * Só quem tem a role dedicada altera o AGR — editar funcionário não serve. Reativo: `can()`
     * deriva do signal de roles, então o controle se habilita sozinho quando as roles chegam.
     */
    protected readonly podeEditarAgr = computed(() => this.permissionService.can(this.roleUsuariosEditarAgr));

    /** Por que o controle está bloqueado, ou `null` se está liberado. */
    protected readonly motivoAgrBloqueado = computed<string | null>(() => (this.podeEditarAgr() ? null : 'Sem permissão para alterar.'));

    protected readonly agrBloqueado = computed(() => this.motivoAgrBloqueado() !== null);

    /**
     * Regional não escolhe UF: a busca do solicitante define a UF do funcionário gravado
     * (`request.uf = solicitante.uf`), e o backend só aceita a do operador. Travado, o formulário
     * deixa de oferecer uma escolha que terminaria em 403.
     */
    protected readonly ufTravada = computed(() => !this.permissionService.isCfm);

    /**
     * Carregamentos do detalhe, serializados por `switchMap`.
     *
     * <p>O diálogo vive fora do `@if` da lista, então nunca é destruído entre aberturas e o
     * `takeUntilDestroyed` não cancela nada. Sem o `switchMap`, a resposta lenta do funcionário A
     * chegaria depois da de B e cairia no formulário de B — que seria salvo na chave de B com os
     * dados de A.
     */
    private readonly carregarDetalhe$ = new Subject<FuncionarioChave>();

    constructor() {
        this.carregarDetalhe$
            .pipe(
                switchMap((chave) => {
                    this.carregando.set(true);
                    return this.service.buscarPorChave(chave.uf, chave.idSolicitante);
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (d) => {
                    this.carregando.set(false);
                    this.solicitante.set({ idSolicitante: d.idSolicitante, uf: d.uf, nmSolicitante: d.nome ?? '', cpf: d.cpf });
                    this.agenteAgr.set(d.inAgenteAgr === 'S');
                    this.form.patchValue({
                        nuMatricula: d.nuMatricula ?? '',
                        idCargo: d.idCargo,
                        idEstruturaOrganizacional: d.idEstruturaOrganizacional,
                        dtNascimento: this.paraData(d.dtNascimento),
                        dtAdmissao: this.paraData(d.dtAdmissao),
                        dtAfastamento: this.paraData(d.dtAfastamento),
                        inConselheiro: d.inConselheiro ?? 'N',
                        inResponsavel: d.inResponsavel ?? 'N',
                        inAscfm: d.inAscfm ?? 'N'
                    });
                },
                error: () => {
                    // Fecha o dialogo, nao so avisa: um formulario em branco com o Salvar habilitado
                    // manda todos os campos como null e APAGA o cadastro que nao conseguiu carregar.
                    this.carregando.set(false);
                    this.showDialog.set(false);
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao carregar o funcionário' });
                }
            });

        effect(() => {
            if (this.showDialog()) this.inicializar();
        });
    }

    get modoEdicao(): boolean {
        return this.chave() !== null;
    }

    get titulo(): string {
        return this.modoEdicao ? 'Editar Funcionário' : 'Novo Funcionário';
    }

    get cpfSolicitante(): string {
        const cpf = this.solicitante()?.cpf;
        return cpf ? MascaraUtil.cpfCnpj(cpf) : 'Não informado';
    }

    /**
     * Zera tudo ao abrir.
     *
     * <p>Não é zelo: nesta família de telas o dado de uma pessoa já vazou três vezes para o cadastro
     * da seguinte, porque o diálogo é reaproveitado entre aberturas. O formulário é recriado, não
     * só resetado.
     */
    private inicializar(): void {
        this.formSubmitted = false;
        this.salvando.set(false);
        this.salvandoAgr.set(false);
        this.agenteAgr.set(false);
        this.solicitante.set(null);
        this.form = this.novoForm();
        this.termoBusca.set('');
        // Regional: a busca já abre na UF dele — a única em que pode cadastrar.
        this.ufBusca.set(this.ufTravada() ? (this.permissionService.ufUsuario ?? '').trim().toUpperCase() : '');
        this.resultados.set([]);
        this.totalResultados.set(0);
        this.buscou.set(false);

        this.carregarListasApoio();

        const chave = this.chave();
        if (chave) {
            this.passo.set('formulario');
            this.recarregarChave(chave);
        } else {
            this.passo.set('buscar-solicitante');
        }
    }

    /** Pede o detalhe de uma chave; o `switchMap` descarta a resposta de qualquer pedido anterior. */
    protected recarregarChave(chave: FuncionarioChave): void {
        this.carregarDetalhe$.next(chave);
    }

    private novoForm(): FormGroup {
        return this.fb.group({
            nuMatricula: ['', [Validators.maxLength(5)]],
            idCargo: [null as number | null],
            idEstruturaOrganizacional: [null as number | null],
            dtNascimento: [null as Date | null],
            dtAdmissao: [null as Date | null],
            dtAfastamento: [null as Date | null],
            inConselheiro: ['N'],
            inResponsavel: ['N'],
            inAscfm: ['N']
        });
    }

    private carregarListasApoio(): void {
        this.cargoService
            .listar()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (cargos) => this.opcoesCargos.set(cargos.map((c) => ({ label: c.dsCargo, value: c.id }))),
                // Avisa em vez de engolir: com a lista vazia, um funcionario COM cargo aparece sem
                // cargo na tela, e o operador nao tem como saber que foi falha de carregamento.
                error: () => {
                    this.opcoesCargos.set([]);
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao carregar a lista de cargos' });
                }
            });

        this.estruturaService
            .listar()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (estruturas) => this.opcoesEstruturas.set(estruturas.map((e) => ({ label: e.descricao, value: e.id }))),
                error: () => {
                    this.opcoesEstruturas.set([]);
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao carregar a lista de lotações' });
                }
            });
    }

    // ── Busca de solicitante ───────────────────────────────────────────────

    /**
     * Busca a pessoa a registrar. <b>Exige UF</b>: a consulta do backend filtra por
     * `s.id.ufSgUf = :uf`, então sem estado a busca casaria com zero linhas e a tela diria "nenhum
     * solicitante encontrado" para alguém que existe — o caminho principal da tela travaria sem
     * pista. Mesma guarda do `pesquisa-solicitante` que já existe no cadastro de usuários.
     */
    protected buscarSolicitante(): void {
        if (!this.ufBusca().trim()) {
            this.notificacoes.notificar({ severity: 'warn', summary: 'Escolha o estado antes de buscar.' });
            return;
        }
        if (this.termoBusca().trim().length < 3) {
            this.notificacoes.notificar({ severity: 'warn', summary: 'Informe ao menos 3 caracteres do nome ou CPF.' });
            return;
        }

        this.buscando.set(true);
        this.usuarioService
            .buscarSolicitante(this.termoBusca().trim(), this.ufBusca().trim().toUpperCase(), 0, this.TAMANHO_BUSCA)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (pagina) => {
                    this.resultados.set(pagina.content ?? []);
                    this.totalResultados.set(pagina.totalElements ?? 0);
                    this.buscando.set(false);
                    this.buscou.set(true);
                },
                error: () => {
                    this.buscando.set(false);
                    this.buscou.set(true);
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao buscar solicitante' });
                }
            });
    }

    /** Há mais gente do que coube na busca: a tela precisa dizer isso, senão o usuário desiste. */
    protected get resultadosTruncados(): boolean {
        return this.totalResultados() > this.resultados().length;
    }

    protected onUfBuscaChange(uf: string): void {
        this.ufBusca.set((uf ?? '').toUpperCase());
    }

    protected selecionarSolicitante(s: SolicitanteSearchResponse): void {
        this.onSolicitanteSelecionado({ idSolicitante: s.idSolicitante, uf: s.uf, nmSolicitante: s.nmSolicitante, cpf: s.cpf ?? null });
    }

    /**
     * Troca a pessoa e <b>zera o formulário</b>. Sem isso, a matrícula de quem foi escolhido antes
     * fica no campo e vai parar no cadastro da pessoa seguinte.
     */
    protected onSolicitanteSelecionado(s: SolicitanteEscolhido): void {
        this.solicitante.set(s);
        this.form = this.novoForm();
        this.formSubmitted = false;
        this.passo.set('formulario');
    }

    protected voltarParaBusca(): void {
        this.solicitante.set(null);
        this.form = this.novoForm();
        this.passo.set('buscar-solicitante');
    }

    // ── Agente de Registro ─────────────────────────────────────────────────

    /**
     * Grava o AGR na hora, pelo endpoint dedicado — fora do salvamento do formulário, porque a role
     * é outra. Falhou, o switch volta ao estado anterior: deixá-lo ligado mentiria sobre o banco.
     */
    protected onAgrChange(valor: boolean): void {
        const chave = this.chave();
        if (!chave || this.agrBloqueado()) return;

        const anterior = this.agenteAgr();
        this.agenteAgr.set(valor);
        this.salvandoAgr.set(true);

        this.service
            .alterarAgenteAgr(chave.uf, chave.idSolicitante, valor)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.salvandoAgr.set(false);
                    this.notificacoes.notificar({ severity: 'success', summary: valor ? 'Funcionário marcado como Agente de Registro.' : 'Marca de Agente de Registro removida.' });
                    // A gravacao ja aconteceu: sem avisar, a lista seguiria exibindo a marca antiga
                    // se o usuario fechasse por Cancelar.
                    this.agrAlterado.emit();
                },
                error: (e: { status?: number }) => {
                    this.agenteAgr.set(anterior);
                    this.salvandoAgr.set(false);
                    if (e?.status === 403) {
                        this.notificacoes.notificar({ severity: 'error', summary: 'Sem permissão para alterar o Agente de Registro.' });
                        return;
                    }
                    const summary = e?.status === 422 ? 'Funcionário sem cadastro de funcionário para marcar.' : 'Erro ao alterar Agente de Registro.';
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary });
                }
            });
    }

    // ── Salvar ─────────────────────────────────────────────────────────────

    protected salvar(): void {
        this.formSubmitted = true;
        if (this.form.invalid) return;

        const chave = this.chave();
        // Sem pessoa escolhida nao ha chave para criar: o request iria sem uf/idSolicitante e o
        // backend recusaria com 400. O botao ja fica desabilitado, mas a guarda e aqui.
        if (!chave && !this.solicitante()) {
            this.notificacoes.notificar({ severity: 'warn', summary: 'Escolha o solicitante antes de salvar.' });
            return;
        }

        // `inAgenteAgr` nao entra no corpo: o backend nem o aceita, e e o que impede esta tela de
        // contornar a role dedicada.
        const request = this.montarRequest();

        this.salvando.set(true);
        const operacao = chave ? this.service.editar(chave.uf, chave.idSolicitante, request) : this.service.criar(request);

        operacao.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: () => {
                this.salvando.set(false);
                this.notificacoes.notificar({ severity: 'success', summary: chave ? 'Funcionário atualizado com sucesso.' : 'Funcionário cadastrado com sucesso.' });
                this.showDialog.set(false);
                this.salvo.emit();
            },
            error: (e: { status?: number }) => {
                this.salvando.set(false);
                this.notificacoes.notificar({ toast: false, severity: 'error', summary: this.mensagemErroSalvar(e?.status) });
            }
        });
    }

    private montarRequest(): FuncionarioRequest {
        const v = this.form.value;
        const request: FuncionarioRequest = {
            nuMatricula: v.nuMatricula || null,
            idCargo: v.idCargo ?? null,
            idEstruturaOrganizacional: v.idEstruturaOrganizacional ?? null,
            dtNascimento: this.paraIso(v.dtNascimento),
            dtAdmissao: this.paraIso(v.dtAdmissao),
            dtAfastamento: this.paraIso(v.dtAfastamento),
            inConselheiro: v.inConselheiro ?? 'N',
            inResponsavel: v.inResponsavel ?? 'N',
            inAscfm: v.inAscfm ?? 'N'
        };

        if (!this.modoEdicao) {
            const s = this.solicitante()!;
            request.idSolicitante = s.idSolicitante;
            request.uf = s.uf;
        }
        return request;
    }

    private mensagemErroSalvar(status?: number): string {
        if (status === 409) return 'Já existe cadastro de funcionário para este solicitante e UF.';
        if (status === 404) return 'Solicitante ou funcionário não encontrado.';
        return 'Erro ao salvar o funcionário.';
    }

    protected get podeSalvar(): boolean {
        return this.modoEdicao || this.solicitante() !== null;
    }

    protected isInvalid(campo: string): boolean {
        const c = this.form.get(campo);
        return (c?.invalid && (c.dirty || c.touched || this.formSubmitted)) ?? false;
    }

    protected fechar(): void {
        this.showDialog.set(false);
    }

    protected cpfDe(cpf: string | null): string {
        return cpf ? MascaraUtil.cpfCnpj(cpf) : '—';
    }

    /** `'2020-01-15'` → `Date` local. Sem o split, o `Date` do ISO puro volta um dia por causa do UTC. */
    private paraData(iso: string | null): Date | null {
        if (!iso) return null;
        const [ano, mes, dia] = iso.split('-').map(Number);
        if (!ano || !mes || !dia) return null;
        return new Date(ano, mes - 1, dia);
    }

    /** `Date` local → `'2020-01-15'`. `toISOString` usaria UTC e mudaria o dia. */
    private paraIso(data: Date | null | undefined): string | null {
        if (!data) return null;
        const mes = String(data.getMonth() + 1).padStart(2, '0');
        const dia = String(data.getDate()).padStart(2, '0');
        return `${data.getFullYear()}-${mes}-${dia}`;
    }
}
